/**
 * RTWO Event Bus — Repositories
 *
 * In-memory stores remain empty until engine source adapter ingests real data.
 */

import type {
  DeadLetterId,
  DeliveryId,
  EventId,
  MetadataId,
  PublisherId,
  RouteId,
  RTWODeadLetterQueue,
  RTWOEvent,
  RTWOEventDelivery,
  RTWOEventMetadata,
  RTWOEventRoute,
  RTWOEventStatus,
  RTWOEventTopic,
  RTWOPublisher,
  RTWOPublisherStatus,
  RTWOSubscriber,
  RTWOSubscriberStatus,
  RTWOTopicStatus,
  SubscriberId,
  TopicId,
} from '../domain-models';

class InMemoryMapRepository<T> {
  private readonly store = new Map<string, T>();

  constructor(private readonly getKey: (entity: T) => string) {}

  findAll(): T[] {
    return Array.from(this.store.values());
  }

  findById(id: string): T | null {
    return this.store.get(id) ?? null;
  }

  save(entity: T): void {
    this.store.set(this.getKey(entity), entity);
  }

  count(): number {
    return this.store.size;
  }
}

export class EventRepository {
  private readonly byId = new InMemoryMapRepository<RTWOEvent>((event) => event.eventId);

  findAll(): RTWOEvent[] {
    return this.byId.findAll();
  }

  findById(eventId: EventId): RTWOEvent | null {
    return this.byId.findById(eventId);
  }

  findByStatus(status: RTWOEventStatus): RTWOEvent[] {
    return this.findAll().filter((event) => event.status === status);
  }

  save(event: RTWOEvent): void {
    this.byId.save(event);
  }

  count(): number {
    return this.byId.count();
  }
}

export class TopicRepository {
  private readonly byId = new InMemoryMapRepository<RTWOEventTopic>((topic) => topic.topicId);

  findAll(): RTWOEventTopic[] {
    return this.byId.findAll();
  }

  findById(topicId: TopicId): RTWOEventTopic | null {
    return this.byId.findById(topicId);
  }

  findByStatus(status: RTWOTopicStatus): RTWOEventTopic[] {
    return this.findAll().filter((topic) => topic.status === status);
  }

  save(topic: RTWOEventTopic): void {
    this.byId.save(topic);
  }

  count(): number {
    return this.byId.count();
  }
}

export class PublisherRepository {
  private readonly byId = new InMemoryMapRepository<RTWOPublisher>(
    (publisher) => publisher.publisherId
  );

  findAll(): RTWOPublisher[] {
    return this.byId.findAll();
  }

  findById(publisherId: PublisherId): RTWOPublisher | null {
    return this.byId.findById(publisherId);
  }

  findByStatus(status: RTWOPublisherStatus): RTWOPublisher[] {
    return this.findAll().filter((publisher) => publisher.status === status);
  }

  save(publisher: RTWOPublisher): void {
    this.byId.save(publisher);
  }

  countActive(): number {
    return this.findAll().filter((publisher) => publisher.status === 'active').length;
  }
}

export class SubscriberRepository {
  private readonly byId = new InMemoryMapRepository<RTWOSubscriber>(
    (subscriber) => subscriber.subscriberId
  );

  findAll(): RTWOSubscriber[] {
    return this.byId.findAll();
  }

  findById(subscriberId: SubscriberId): RTWOSubscriber | null {
    return this.byId.findById(subscriberId);
  }

  findByStatus(status: RTWOSubscriberStatus): RTWOSubscriber[] {
    return this.findAll().filter((subscriber) => subscriber.status === status);
  }

  save(subscriber: RTWOSubscriber): void {
    this.byId.save(subscriber);
  }

  countActive(): number {
    return this.findAll().filter((subscriber) => subscriber.status === 'active').length;
  }
}

export class RouteRepository {
  private readonly byId = new InMemoryMapRepository<RTWOEventRoute>((route) => route.routeId);

  findAll(): RTWOEventRoute[] {
    return this.byId.findAll();
  }

  findById(routeId: RouteId): RTWOEventRoute | null {
    return this.byId.findById(routeId);
  }

  save(route: RTWOEventRoute): void {
    this.byId.save(route);
  }
}

export class DeliveryRepository {
  private readonly byId = new InMemoryMapRepository<RTWOEventDelivery>(
    (delivery) => delivery.deliveryId
  );

  findAll(): RTWOEventDelivery[] {
    return this.byId.findAll();
  }

  findById(deliveryId: DeliveryId): RTWOEventDelivery | null {
    return this.byId.findById(deliveryId);
  }

  save(delivery: RTWOEventDelivery): void {
    this.byId.save(delivery);
  }

  countPending(): number {
    return this.findAll().filter((delivery) => delivery.deliveryStatus === 'pending').length;
  }

  countFailed(): number {
    return this.findAll().filter((delivery) => delivery.deliveryStatus === 'failed').length;
  }
}

export class DeadLetterRepository {
  private readonly byId = new InMemoryMapRepository<RTWODeadLetterQueue>((entry) => entry.queueId);

  findAll(): RTWODeadLetterQueue[] {
    return this.byId.findAll();
  }

  findById(queueId: DeadLetterId): RTWODeadLetterQueue | null {
    return this.byId.findById(queueId);
  }

  save(entry: RTWODeadLetterQueue): void {
    this.byId.save(entry);
  }

  count(): number {
    return this.byId.count();
  }
}

export class MetadataRepository {
  private readonly byEvent = new InMemoryMapRepository<RTWOEventMetadata>(
    (metadata) => metadata.eventId
  );
  private readonly byId = new InMemoryMapRepository<RTWOEventMetadata>(
    (metadata) => metadata.metadataId
  );

  findByEventId(eventId: EventId): RTWOEventMetadata | null {
    return this.byEvent.findById(eventId);
  }

  findById(metadataId: MetadataId): RTWOEventMetadata | null {
    return this.byId.findById(metadataId);
  }

  save(metadata: RTWOEventMetadata): void {
    this.byEvent.save(metadata);
    this.byId.save(metadata);
  }
}

export interface EBusRepositories {
  events: EventRepository;
  topics: TopicRepository;
  publishers: PublisherRepository;
  subscribers: SubscriberRepository;
  routes: RouteRepository;
  deliveries: DeliveryRepository;
  deadLetters: DeadLetterRepository;
  metadata: MetadataRepository;
}

export function createEBusRepositories(): EBusRepositories {
  return {
    events: new EventRepository(),
    topics: new TopicRepository(),
    publishers: new PublisherRepository(),
    subscribers: new SubscriberRepository(),
    routes: new RouteRepository(),
    deliveries: new DeliveryRepository(),
    deadLetters: new DeadLetterRepository(),
    metadata: new MetadataRepository(),
  };
}
